import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BAZI_KEY = 'mingpan_bazi';
const DAYUN_KEY = 'mingpan_dayun';

const BaziContext = createContext(null);

export function BaziProvider({ children }) {
  const [baziData, setBaziDataState] = useState(null);
  const [dayunData, setDayunDataState] = useState(null);
  const [returnTo, setReturnTo] = useState(null);
  const [isHydrated, setIsHydrated] = useState(false);

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(BAZI_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        setBaziDataState(data);
      }
      const storedDayun = localStorage.getItem(DAYUN_KEY);
      if (storedDayun) {
        setDayunDataState(JSON.parse(storedDayun));
      }
    } catch {
      // corrupted data, ignore
    } finally {
      setIsHydrated(true);
    }
  }, []);

  const setBaziData = useCallback((data) => {
    setBaziDataState(data);
    if (data) {
      localStorage.setItem(BAZI_KEY, JSON.stringify(data));
    } else {
      localStorage.removeItem(BAZI_KEY);
    }
  }, []);

  const setDayunData = useCallback((data) => {
    setDayunDataState(data);
    if (data) {
      localStorage.setItem(DAYUN_KEY, JSON.stringify(data));
    } else {
      localStorage.removeItem(DAYUN_KEY);
    }
  }, []);

  const clearBaziData = useCallback(() => {
    setBaziDataState(null);
    setDayunDataState(null);
    setReturnTo(null);
    localStorage.removeItem(BAZI_KEY);
    localStorage.removeItem(DAYUN_KEY);
  }, []);

  const isBaziLoaded = !!baziData;

  // Get four-pillar summary string like "甲子 丙寅 己巳 庚午"
  const getPillarSummary = useCallback(() => {
    if (!baziData?.four_pillars) return '';
    const p = baziData.four_pillars;
    return `${p.year?.gan || ''}${p.year?.zhi || ''} ${p.month?.gan || ''}${p.month?.zhi || ''} ${p.day?.gan || ''}${p.day?.zhi || ''} ${p.hour?.gan || ''}${p.hour?.zhi || ''}`.trim();
  }, [baziData]);

  // Get useful god string from favorable elements
  const getUsefulGod = useCallback(() => {
    if (!baziData?.favorable_elements?.length) return '—';
    return baziData.favorable_elements.join('、');
  }, [baziData]);

  const value = {
    baziData,
    dayunData,
    setBaziData,
    setDayunData,
    clearBaziData,
    isBaziLoaded,
    isHydrated,
    returnTo,
    setReturnTo,
    getPillarSummary,
    getUsefulGod,
  };

  return (
    <BaziContext.Provider value={value}>
      {children}
    </BaziContext.Provider>
  );
}

export function useBazi() {
  const ctx = useContext(BaziContext);
  if (!ctx) throw new Error('useBazi must be used within BaziProvider');
  return ctx;
}

export default BaziContext;
